/*
 * Scroll-linked text fill.
 *
 * data-scroll-fill
 *   On a heading or paragraph: that text fills as it scrolls.
 *   Several of these in one Shopify section fill in document order:
 *   the next block starts only after the previous one is full.
 *   On a container: every heading and paragraph inside fills, in order.
 * data-scroll-fill-text
 *   Inside a data-scroll-fill section, only these elements fill.
 * data-scroll-fill-ignore
 *   Skip this element and everything inside it.
 * data-scroll-fill-opacity
 *   Unfilled opacity: 0.37 or 37.
 * data-scroll-fill-line
 *   Optional pre-split line. Otherwise <br> and block children are split automatically.
 */
(function () {
  var items = new Set();
  var listening = false;
  var ticking = false;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      updateAll();
    });
  }

  function listen() {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    reduceMotion.addEventListener('change', onMotionChange);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        items.forEach(relayout);
        updateAll();
      });
    }
  }

  var lastWidth = window.innerWidth;
  var resizeTimer = 0;

  function onResize() {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      items.forEach(relayout);
      updateAll();
    }, 150);
  }

  function onMotionChange() {
    if (!reduceMotion.matches) return;
    items.forEach(function (item) {
      item.root.classList.add('scroll-fill--static');
    });
  }

  function ghostPercent(value) {
    var number = parseFloat(value);
    if (isNaN(number)) return null;
    var percent = number > 1 ? number : number * 100;
    return Math.min(100, Math.max(0, percent)) + '%';
  }

  function isSkipped(el, root) {
    if (el.closest('[data-scroll-fill-ignore]')) return true;
    if (el.closest('a, button, form, summary')) return true;
    if (el.querySelector('img, svg, video, iframe, input, button')) return true;
    return el.closest('[data-scroll-fill]') !== root;
  }

  function blocksFor(root) {
    var explicit = Array.prototype.filter.call(root.querySelectorAll('[data-scroll-fill-text]'), function (el) {
      return !isSkipped(el, root);
    });
    if (explicit.length) return explicit;

    var auto = Array.prototype.filter.call(root.querySelectorAll('h1, h2, h3, h4, h5, h6, p, blockquote'), function (el) {
      return !isSkipped(el, root) && el.textContent.trim() !== '';
    });
    if (auto.length) return auto;
    if (root.closest('[data-scroll-fill-ignore]')) return [];
    return [root];
  }

  function linesFromBlock(block) {
    var ready = block.querySelectorAll(':scope > [data-scroll-fill-line], :scope > .scroll-fill__line');
    if (ready.length) {
      Array.prototype.forEach.call(ready, function (line) {
        line.classList.add('scroll-fill__line');
      });
      return Array.prototype.slice.call(ready);
    }

    if (/<br\s*\/?>/i.test(block.innerHTML) && !block.querySelector('p, div, h1, h2, h3, h4, h5, h6, ul, ol')) {
      var parts = block.innerHTML.split(/<br\s*\/?>/i).map(function (part) {
        return part.trim();
      }).filter(Boolean);
      if (parts.length > 1) {
        block.innerHTML = parts.map(function (part) {
          return '<span class="scroll-fill__line" data-scroll-fill-line>' + part + '</span>';
        }).join('');
        return Array.prototype.slice.call(block.children);
      }
    }

    block.classList.add('scroll-fill__line');
    block.setAttribute('data-scroll-fill-line', '');
    return [block];
  }

  function visualGroups(textNode) {
    var text = textNode.textContent;
    var range = document.createRange();
    var groups = [];
    var current = '';
    var lastTop = null;

    for (var i = 0; i < text.length; i++) {
      range.setStart(textNode, i);
      range.setEnd(textNode, i + 1);
      var rect = range.getClientRects()[0];
      var top = rect ? Math.round(rect.top) : lastTop;

      if (current && lastTop !== null && top !== null && Math.abs(top - lastTop) > 4) {
        groups.push(current);
        current = text.charAt(i);
      } else {
        current += text.charAt(i);
      }

      if (top !== null) lastTop = top;
    }

    if (current) groups.push(current);

    return groups.map(function (part) {
      return part.replace(/^\s+|\s+$/g, '');
    }).filter(Boolean);
  }

  function expandSource(source) {
    var text = source.dataset.scrollFillText || source.textContent;
    source.dataset.scrollFillText = text;
    source.textContent = text;
    source.classList.remove('scroll-fill__source');
    source.classList.add('scroll-fill__line');

    var node = source.firstChild;
    if (!node || node.nodeType !== Node.TEXT_NODE) return [source];

    var groups = visualGroups(node);
    if (groups.length < 2) return [source];

    source.classList.remove('scroll-fill__line');
    source.classList.add('scroll-fill__source');
    source.textContent = '';

    return groups.map(function (part) {
      var span = document.createElement('span');
      span.className = 'scroll-fill__line';
      span.textContent = part;
      alignLine(span, source);
      source.appendChild(span);
      return span;
    });
  }

  function relayout(item) {
    if (!item.root.isConnected) {
      items.delete(item);
      return;
    }

    var lines = [];
    item.sources.forEach(function (source) {
      if (!source.isConnected) return;
      expandSource(source).forEach(function (line) {
        lines.push(line);
      });
    });

    item.lines = lines;
    item.lengths = lines.map(function (line) {
      return Math.max(line.textContent.trim().length, 1);
    });
  }

  function alignLine(line, source) {
    var align = getComputedStyle(source).textAlign;
    if (align === 'center') {
      line.style.marginInline = 'auto';
    } else if (align === 'right') {
      line.style.marginLeft = 'auto';
      line.style.marginRight = '0';
    } else if (align === 'left') {
      line.style.marginRight = 'auto';
      line.style.marginLeft = '0';
    }
  }

  function setup(root) {
    if (root.dataset.scrollFillReady === 'true') return;
    if (root.closest('[data-scroll-fill-ignore]')) return;

    var blocks = blocksFor(root);
    if (!blocks.length) return;

    root.dataset.scrollFillReady = 'true';

    var direction = getComputedStyle(root).direction === 'rtl' ? 'to left' : 'to right';
    root.style.setProperty('--scroll-fill-direction', direction);

    var opacity = ghostPercent(root.getAttribute('data-scroll-fill-opacity'));
    if (opacity) root.style.setProperty('--scroll-fill-ghost', opacity);

    if (!root.style.getPropertyValue('--scroll-fill-color')) {
      root.style.setProperty('--scroll-fill-color', getComputedStyle(blocks[0]).color);
    }

    var lines = [];
    blocks.forEach(function (block) {
      linesFromBlock(block).forEach(function (line) {
        alignLine(line, block);
        lines.push(line);
      });
    });

    if (!lines.length) return;

    if (reduceMotion.matches || root.hasAttribute('data-scroll-fill-static')) {
      root.classList.add('scroll-fill--static');
      return;
    }

    var item = {
      root: root,
      sources: lines,
      lines: lines,
      lengths: []
    };
    items.add(item);
    listen();
    relayout(item);
  }

  function paintGroup(group) {
    var lines = [];
    var lengths = [];

    group.forEach(function (item) {
      (item.lines || []).forEach(function (line, index) {
        if (!line.isConnected) return;
        lines.push(line);
        lengths.push(item.lengths[index] || 1);
      });
    });

    if (!lines.length) return;

    var first = lines[0].getBoundingClientRect();
    var last = lines[lines.length - 1].getBoundingClientRect();
    var view = window.innerHeight || 1;
    var start = view * 0.82;
    var end = view * 0.32;
    var height = Math.max(last.bottom - first.top, 1);
    var distance = start - end + height;
    var progress = Math.min(1, Math.max(0, (start - first.top) / distance));
    var total = lengths.reduce(function (sum, length) { return sum + length; }, 0);
    var cursor = 0;

    lines.forEach(function (line, index) {
      var span = lengths[index] / total;
      var local = span === 0 ? 1 : (progress - cursor) / span;
      cursor += span;
      line.style.setProperty('--scroll-fill', Math.min(1, Math.max(0, local)).toFixed(4));
    });
  }

  function updateAll() {
    var groups = new Map();

    items.forEach(function (item) {
      if (!item.root.isConnected) {
        items.delete(item);
        return;
      }

      var section = item.root.closest('.shopify-section') || item.root;
      if (!groups.has(section)) groups.set(section, []);
      groups.get(section).push(item);
    });

    groups.forEach(function (group) {
      group.sort(function (a, b) {
        if (a.root === b.root) return 0;
        var position = a.root.compareDocumentPosition(b.root);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
      paintGroup(group);
    });
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches('[data-scroll-fill]')) setup(context);
    context.querySelectorAll('[data-scroll-fill]').forEach(setup);
    updateAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('page:loaded', function () {
    init(document);
  });
})();
